import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    Box,
    Button,
    CircularProgress,
    Container,
    CssBaseline,
    Stack,
    TextField,
    ThemeProvider,
    Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'

import { useLazyGetAuthProviderQuery } from '@/app/services/authProviderApi'
import { AppLogo } from '@/common/components'
import {
    AUTH_PROVIDERS,
    getRegistrationProvider,
    isEmailFirstLoginEnabled,
    setAuthProvider,
} from '@/common/helpers/auth/authProviderDetection'
import { useToolBoxTreatments } from '@/common/hooks'
import { useAuth } from '@/common/hooks/useAuth'
import { theme as lightTheme } from '@/common/themes/LightTheme'
import { StrikerThemeOverride } from '@/common/themes/overrides/StrikerThemeOverride'
import { WhitelabelThemeOverride } from '@/common/themes/overrides/WhitelabelThemeOverride'
import { EMAIL_PATTERN, getProductDetails } from '@/common/utils'

// Match the TechnicalBackground styling from TbxLoadingPage/SignOut
const TechnicalBackground = styled(Box)(({ theme }) => ({
    background: theme.palette.background.default,
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    position: 'relative',
}))

// Match the TechnicalContainer styling from TbxLoadingPage/SignOut
const TechnicalContainer = styled(Container)(({ theme }) => ({
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: '8px',
    padding: theme.spacing(4),

    '&::after': {
        content: '"DRAWING NO: TT-LOGIN-001 | SCALE: 1:1 | REV: A"',
        position: 'absolute',
        bottom: -12,
        right: 24,
        fontSize: '12px',
        color: theme.palette.text.secondary,
        background: theme.palette.background.paper,
        padding: '2px 8px',
        border: `1px solid ${theme.palette.action.disabled}`,
        borderRadius: '4px',
    },
}))

// Match the TechnicalContentContainer styling from TbxLoadingPage/SignOut
const TechnicalContentContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.active}`,
    padding: theme.spacing(3),
    borderRadius: '8px',
}))

// Match the TechnicalTypography styling from TbxLoadingPage/SignOut
const TechnicalTypography = styled(Typography)({
    letterSpacing: '0.5px',
})

/**
 * Login - Email-first authentication page for provider detection
 *
 * This component implements the email verification step in the login flow that
 * checks the backend to determine which auth provider (Azure AD B2C or Kinde)
 * to use, then redirects to the correct provider with the email pre-filled.
 *
 * Flow:
 * 1. User enters email
 * 2. API call to check provider (B2C or Kinde)
 * 3. Cache provider in localStorage
 * 4. Redirect with login_hint parameter
 * 5. UserRequiredContainer handles actual login with pre-filled email
 *
 * @returns {JSX.Element}
 */
const Login = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const { showLibellulaWhitelabelling, showStrikerWhitelabelling } = useToolBoxTreatments()
    const productDetails = getProductDetails()
    const { isAuthenticated, isLoading: isAuthLoading, register } = useAuth()

    // API hook for provider detection
    const [getAuthProvider, { error: apiError, isLoading: isApiLoading }] = useLazyGetAuthProviderQuery()

    // Local error state for display
    const [displayError, setDisplayError] = useState(null)
    // Track when redirecting to registration to suppress error display
    const [isRedirecting, setIsRedirecting] = useState(false)

    // Get return URL from navigation state (set by UserRequiredContainer).
    // Validate it is a relative path to prevent open redirect to external URLs.
    const rawReturnTo = location.state?.returnTo || '/'
    const safeReturnTo = typeof rawReturnTo === 'string' && rawReturnTo.startsWith('/') ? rawReturnTo : '/'

    // Theme selection for white-labeling
    const theme = useMemo(
        () =>
            showLibellulaWhitelabelling
                ? WhitelabelThemeOverride(lightTheme)
                : showStrikerWhitelabelling
                  ? StrikerThemeOverride(lightTheme)
                  : lightTheme,
        [showLibellulaWhitelabelling, showStrikerWhitelabelling]
    )

    // Form setup with react-hook-form
    const {
        control,
        formState: { errors, isValid },
        handleSubmit,
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            email: '',
        },
    })

    // QM-3353: When email-first detection is disabled (post-migration), the
    // /login page is unreachable by design — bounce to / so UserRequiredContainer
    // runs the standard auth flow into the configured default provider.
    // Form code stays intact so Libellula can re-enable it via the env flag.
    useEffect(() => {
        if (!isEmailFirstLoginEnabled()) {
            navigate('/', { replace: true })
        }
    }, [navigate])

    // Redirect authenticated users to home
    useEffect(() => {
        if (isAuthenticated && !isAuthLoading) {
            navigate('/', { replace: true })
        }
    }, [isAuthenticated, isAuthLoading, navigate])

    /**
     * Handle form submission - lookup provider and redirect
     *
     * @param {Object} data - Form data containing email
     */
    const onSubmit = async (data) => {
        setDisplayError(null)

        try {
            const result = await getAuthProvider(data.email).unwrap()

            // Check if provider was found - undefined means unknown provider type
            // Redirect new users to registration via configured provider
            if (!result.authProvider) {
                const registrationProvider = getRegistrationProvider()
                console.warn(
                    '[Login] No auth provider found for email - redirecting to registration via',
                    registrationProvider
                )
                setAuthProvider(registrationProvider)

                if (registrationProvider === AUTH_PROVIDERS.MSAL) {
                    // MSAL already mounted — call register() directly
                    await register({ loginHint: data.email, login_hint: data.email })
                } else {
                    // Full reload needed so AuthProvider remounts with Kinde
                    setIsRedirecting(true)
                    window.location.href = `/register?login_hint=${encodeURIComponent(data.email)}`
                }
                return
            }

            // Cache the provider for future visits
            setAuthProvider(result.authProvider)

            // Build redirect URL with login_hint.
            // Use window.location.href for full page reload so AuthProvider remounts with correct SDK.
            // NOTE (QM-3136): login_hint exposes the email in the URL (browser history, server logs).
            // This is accepted risk: login_hint is a standard OAuth 2.0 parameter, the value is
            // transient (URL is replaced once UserRequiredContainer calls login()), and the email
            // was already entered by the user moments ago. Alternative (session storage) would
            // require coordinated changes across Login + UserRequiredContainer with no clear gain.
            const redirectUrl = new URL(safeReturnTo, window.location.origin)
            redirectUrl.searchParams.set('login_hint', data.email)

            window.location.href = redirectUrl.toString()
        } catch (error) {
            console.error('[Login] Provider lookup failed:', error)

            // Redirect to registration for 404 (user not found)
            // Check multiple possible locations for HTTP status (RTK Query error structure varies)
            const httpStatus = error?.status ?? error?.error?.status ?? error?.originalStatus
            const isNotFoundStatus = httpStatus === 404 || httpStatus === '404'
            const isNotFoundMessage = error?.data?.message?.toLowerCase?.()?.includes('not found')
            const isUserNotFound = isNotFoundStatus || isNotFoundMessage

            if (isUserNotFound) {
                const registrationProvider = getRegistrationProvider()
                console.info('[Login] User not found - redirecting to registration via', registrationProvider)
                setIsRedirecting(true)
                setAuthProvider(registrationProvider)

                if (registrationProvider === AUTH_PROVIDERS.MSAL) {
                    // MSAL already mounted — call register() directly
                    await register({ loginHint: data.email, login_hint: data.email })
                } else {
                    // Full reload needed so AuthProvider remounts with Kinde
                    window.location.href = `/register?login_hint=${encodeURIComponent(data.email)}`
                }
                return
            }

            // Show error for server/network issues (user should retry)
            setDisplayError(error?.data?.message || t('An error occurred. Please try again.'))
        }
    }

    // Show loading while checking auth state
    if (isAuthLoading) {
        return null
    }

    // Don't render for authenticated users (useEffect handles redirect)
    if (isAuthenticated) {
        return null
    }

    const isLoading = isApiLoading

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <TechnicalBackground>
                <TechnicalContainer maxWidth="sm">
                    <Stack spacing={4}>
                        <AppLogo logoUrl={productDetails.logo} />

                        {/* Technical Content Section */}
                        <TechnicalContentContainer>
                            <Stack
                                alignItems="center"
                                direction="row"
                                justifyContent="center"
                                mb={2}
                            >
                                <TechnicalTypography
                                    color={theme.palette.text.secondary}
                                    sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                                    variant="body2"
                                >
                                    {t('SIGN IN')}
                                </TechnicalTypography>
                            </Stack>

                            {/* Email Form */}
                            <form
                                id="login-form"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <Stack spacing={3}>
                                    <TechnicalTypography
                                        align="center"
                                        color={theme.palette.text.secondary}
                                        variant="body2"
                                    >
                                        {t('Enter your email address to continue')}
                                    </TechnicalTypography>

                                    <Controller
                                        control={control}
                                        name="email"
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                autoComplete="email"
                                                disabled={isLoading}
                                                error={!!errors.email}
                                                helperText={errors.email?.message}
                                                label={t('Email address')}
                                                size="small"
                                                type="email"
                                                variant="outlined"
                                                autoFocus
                                                fullWidth
                                            />
                                        )}
                                        rules={{
                                            required: t('Email is required'),
                                            pattern: {
                                                value: EMAIL_PATTERN,
                                                message: t('Please enter a valid email address'),
                                            },
                                        }}
                                    />

                                    {/* Error message - hide during redirect to registration */}
                                    {!isRedirecting && (displayError || apiError) ? (
                                        <TechnicalTypography
                                            align="center"
                                            color="error"
                                            variant="body2"
                                        >
                                            {displayError || t('An error occurred. Please try again.')}
                                        </TechnicalTypography>
                                    ) : null}

                                    <Button
                                        color="primary"
                                        disabled={!isValid || isLoading}
                                        form="login-form"
                                        id="login-submit-button"
                                        size="large"
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                    >
                                        {isLoading ? (
                                            <CircularProgress
                                                color="inherit"
                                                size={24}
                                            />
                                        ) : (
                                            t('Continue')
                                        )}
                                    </Button>
                                </Stack>
                            </form>
                        </TechnicalContentContainer>
                    </Stack>
                </TechnicalContainer>
            </TechnicalBackground>
        </ThemeProvider>
    )
}

export default Login
